import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, List, ListItem, ListItemText, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { isEmpty } from 'lodash';
import logo from '../assets/logo.svg';

const sections = [
    {
        title: '1. Trial Access',
        description:
            '<strong>“Licensed Software (Service)”</strong> means the proprietary software applications owned or distributed by PSI that are made available to Licensee as a hosted, cloud-based service (the “Service”) over the internet, for which Licensee is granted a right to access and use under the terms of this Agreement.\n\nPSI grants a <strong>limited, non-exclusive, non-transferable, revocable</strong> license to access the PSI Trial Service <strong>only for internal evaluation, non-production,</strong> and <strong>temporary</strong> use during the trial period specified at signup.',
    },
    {
        title: '2. Restrictions',
        description: (
            <>
                User agrees <strong>NOT</strong> to:
            </>
        ),
        list: [
            'Use the Trial Service for production, commercial, or client-facing activities',
            'Share login credentials',
            'Upload sensitive, personal, or regulated data',
            'Copy, modify, reverse engineer, or attempt to access source code',
            'Exceed usage limits communicated during signup',
            'Use the Licensed Software in any way not expressly provided for in this Agreement',
            'Use the Trial Service after the trial expires',
        ],
    },
    {
        title: '3. No Fees',
        description:
            'The Trial Service is provided at <strong>no charge</strong> during the trial period. Any subscription or continued use requires acceptance of PSI’s full commercial license agreement.',
    },
    {
        title: '4. Data & Confidentiality',
        description:
            'User agrees to keep confidential all information related to the Trial Service, including features, documentation, pricing, performance, or feedback. PSI may delete trial data after the trial ends.',
    },
    {
        title: '5. Ownership',
        description:
            'PSI retains all worldwide patent rights, copyrights, trade marks, trade secrets, know-how and any other intellectual property rights recognized in any country or jurisdiction in the world rights to the Trial Service, software, and materials. This Agreement provides no ownership or intellectual property transfer.',
    },
    {
        title: '6. Warranty Disclaimer',
        description:
            'The Trial Service is provided <strong>“AS IS”</strong> with <strong>no warranties</strong>, express or implied, including uninterrupted operation or fitness for any purpose.',
    },
    {
        title: '7. Liability Limitation',
        description:
            'To the fullest extent permitted by law, PSI shall not be liable for any damages arising from trial use. PSI’s maximum liability shall not exceed <strong>USD 10</strong>.',
    },
    {
        title: '8. Termination',
        description:
            'PSI may suspend or terminate trial access at any time. Upon expiration or termination, User must stop all use and delete any materials derived from the Trial Service.',
    },
    {
        title: '9. Governing Law',
        description:
            'This Agreement is governed by the laws of <strong>Jaipur, India,</strong> and disputes shall be subject to exclusive jurisdiction of courts in Jaipur.',
    },
    {
        title: '10. Acceptance',
        description:
            '<strong>By clicking “I Agree”, the User confirms that they have read, understood, and accepted this Agreement.</strong>',
    },
];

const renderSectionDescription = (description) => {
    if (typeof description === 'string') {
        return (
            <Typography
                variant="body1"
                color="text.primary"
                component="div"
                sx={{ '& strong': { fontWeight: 600 } }}
                dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br />') }}
            />
        );
    }

    return (
        <Typography variant="body1" color="text.primary">
            {description}
        </Typography>
    );
};

const TermsConditions = () => {
    const navigate = useNavigate();
    return (
        <Box
            variant="div"
            className="terms-conditions"
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: '#fff',
                zIndex: 1,
            }}
        >
            <Container maxWidth="md" className="container">
                <Box sx={{ px: { xs: 1, md: 2 }, my: 5, pb: 5 }}>
                    <Box className="header">
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                            sx={{ minWidth: 'auto', border: 0 }}
                        >
                            Back
                        </Button>
                        <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                            <img src={logo} alt="Logo" className="logo" width={200} height={100} />
                        </Box>
                        <Box sx={{ width: 80 }} />
                    </Box>
                    <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
                        Trial / Evaluation License Agreement
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        align="justify"
                        gutterBottom
                        sx={{ whiteSpace: 'pre-line', my: 3 }}
                    >
                        By clicking “I Agree”, “Accept”, “Sign Up”, or by accessing or using the Trial Service, you (“User”)
                        agree to be bound by this Trial / Evaluation License Agreement (“Agreement”) with Pratham Software
                        Pvt. Ltd. (“PSI”) – CIN U72200RJ2004PTC019576 of G1-265-266, RIICO Industrial Area, EPIP, Sitapura,
                        Jaipur-302022, Rajasthan, India, Tel: +91 141 6690000, e-mail : info@thepsi.com, www.thepsi.com. If
                        you do not agree, do not use the Trial Service.
                    </Typography>

                    {sections.map((section) => (
                        <Box key={section.title} sx={{ mb: 4 }}>
                            <Typography variant="h6" fontWeight={'bold'} gutterBottom>
                                {section.title}
                            </Typography>
                            {renderSectionDescription(section.description)}
                            {!isEmpty(section?.list) && (
                                <List dense sx={{ mt: 1, pl: 5, listStyleType: 'disc' }}>
                                    {section?.list?.map((item) => (
                                        <ListItem
                                            key={item}
                                            sx={{
                                                display: 'list-item',
                                                py: 0,
                                            }}
                                        >
                                            <ListItemText primary={item} />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default TermsConditions;
